import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'

import { cn } from '../lib/utils'
import { AttentionDot } from './attention-dot'

export interface DotLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Show the attention dot. */
  dot?: boolean
  /** What the dot means, read to screen readers. Set it whenever `dot` is. */
  dotLabel?: string
  /** Render as the passed child (e.g. a router Link) instead of an <a>. */
  asChild?: boolean
}

/**
 * A link that can carry an attention dot — "there is something new behind this".
 *
 * The component owns only the dot. Everything about the link's own type, colour and spacing
 * comes through `className`, so it drops into any surface.
 */
export const DotLink = React.forwardRef<HTMLAnchorElement, DotLinkProps>(function DotLink(
  { dot = false, dotLabel, asChild = false, className, children, ...props },
  ref
) {
  const classes = cn('inline-flex items-center gap-1.5', className)
  const mark = dot ? <AttentionDot label={dotLabel} /> : null

  // asChild: clone the single child and inject the composed content as ITS children, so Slot
  // only ever receives one element — the same shape PillButton uses.
  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<{ children?: React.ReactNode }>
    return (
      <Slot className={classes} ref={ref} {...props}>
        {React.cloneElement(
          child,
          undefined,
          <>
            {child.props.children}
            {mark}
          </>
        )}
      </Slot>
    )
  }

  return (
    <a className={classes} ref={ref} {...props}>
      {children}
      {mark}
    </a>
  )
})
